import { Box, Card, styled, Button, TextField, InputAdornment, IconButton, Grid,Typography, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import { useEffect, useState, useCallback } from "react";
import FieldVisitTable from "page-sections/FieldVisit/FieldVisitTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { Add, GetApp, Search, Clear as ClearIcon,Refresh } from "@mui/icons-material";
import FieldVisitExportModal from "page-sections/FieldVisit/FieldVisitExportModal";
import CropHubTable from "page-sections/CropHub/CropHubTable";
import CropHubAddUpdate from "page-sections/CropHub/CropHubAddUpdate";
// ─── CHANGE 1: Import buttonConfig ─────────────────────────────────────────────
import buttonConfig from "config/ConfigAccessButton";
// ─────────────────────────────────────────────────────────────────────────────

const CropHub = () => {
    const { setPageTitle } = usePageTitle();
    const [fieldVisits, setFieldVisits] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    
    const [pagination, setPagination] = useState({
        current_page: 1,
        page_size: 10,
        total_items: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        next_page_number: null,
        previous_page_number: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [10, 25, 50, 100];
    
    const handleRefresh = () => {
        setRefreshKey((prev) => prev + 1);
    };

    useEffect(() => {
        setPageTitle('Crop Hub');
    }, [setPageTitle]);

    // ─── CHANGE 2: Setup permissions from config ─────────────────────────────
    const cropHubConfig = buttonConfig["Crop hub"] || {};
    const can = (action) => !!cropHubConfig[action];
    // ─────────────────────────────────────────────────────────────────────────

    const fetchFieldVisits = useCallback(async (search = searchValue, page = currentPage, pageSizeParam = pageSize) => {
        setLoading(true);
        try {
            let url = API_ENDPOINTS.GET_field_visit;
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('page_size', String(pageSizeParam));
            // if (search) {
            //     params.set('search', search);
            // }
            url += (url.includes('?') ? '&' : '?') + params.toString();
            
            const response = await apiService.get(url);

            if (response?.success && response?.data?.FieldVisit) {
                setFieldVisits(response.data.FieldVisit);
            } else {
                setFieldVisits([]);
            }
            
            const paginationData = response?.pagination || response?.data?.pagination || {};
            if (Object.keys(paginationData).length > 0) {
                setPagination({
                    current_page: paginationData.current_page ?? page,
                    page_size: paginationData.page_size ?? pageSizeParam,
                    total_items: paginationData.total_items ?? 0,
                    total_pages: paginationData.total_pages ?? 1,
                    has_next: paginationData.has_next ?? false,
                    has_previous: paginationData.has_previous ?? false,
                    next_page_number: paginationData.next_page_number ?? null,
                    previous_page_number: paginationData.previous_page_number ?? null,
                });
                setCurrentPage(paginationData.current_page ?? page);
                setPageSize(paginationData.page_size ?? pageSizeParam);
            } else {
                const list = response?.data?.FieldVisit || [];
                setPagination(prev => ({
                    ...prev,
                    total_items: list.length,
                    total_pages: 1,
                    current_page: 1,
                    page_size: pageSizeParam,
                }));
            }
        } catch (error) {
            console.error("API error:", error);
            setFieldVisits([]);
        } finally {
            setLoading(false);
        }
    }, [searchValue, currentPage, pageSize]);

    useEffect(() => {
        fetchFieldVisits(searchValue, currentPage, pageSize);
    }, [fetchFieldVisits, searchValue, refreshKey, currentPage, pageSize]);

    const handleSearch = () => {
        const value = searchInput.trim();
        if (!value) {
            handleClearSearch();
            return;
        }
        setSearchValue(value);
        setCurrentPage(1);
        setHasSearched(true);
        fetchFieldVisits(value, 1, pageSize);
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setSearchValue("");
        setCurrentPage(1);
        setHasSearched(false);
        fetchFieldVisits("", 1, pageSize);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };
    
    const handlePageChange = (_, value) => {
        setCurrentPage(value);
        fetchFieldVisits(searchValue, value, pageSize);
    };
    
    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setCurrentPage(1);
        fetchFieldVisits(searchValue, 1, newSize);
    };

    const handleExportClick = () => {
        setExportModalOpen(true);
    };

    const handleExportModalClose = () => {
        setExportModalOpen(false);
    };

    return (
        <Box pt={2} pb={4}>
            <Card sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            placeholder="Search by Crop Name, Scientific Name, Countries"
                            value={searchInput}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearchInput(value);
                                if (hasSearched && value !== searchValue) {
                                    setHasSearched(false);
                                }
                                if (!value.trim() && (searchValue || hasSearched)) {
                                    handleClearSearch();
                                }
                            }}
                            onKeyPress={handleKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput && (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            size="small" 
                                            onClick={handleClearSearch} 
                                            edge="end"
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { height: 40 }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 40,
                                    borderRadius: 2
                                }
                            }}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleSearch}
                                startIcon={<Search />}
                                disabled={!searchInput.trim() || (searchInput === searchValue && hasSearched)}
                                sx={{ 
                                    borderRadius: 2, 
                                    textTransform: "none", 
                                    height: 40,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Search
                            </Button>
                            
                            {/* ─── CHANGE 3: Add Crop button — disabled when no add_crop permission ─── */}
                            <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                startIcon={<Add />}
                                onClick={() => can('add_crop') && handleExportClick()}
                                disabled={!can('add_crop')}
                                sx={{ 
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    height: 40,
                                    boxShadow: 'none',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    '&:hover': {
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Add Crop
                            </Button>
                            {/* ─────────────────────────────────────────────────────────────────────── */}

                            <Button
                                variant="contained"
                                onClick={handleRefresh}
                                startIcon={<Refresh />}
                                sx={{ 
                                    minWidth: "auto",
                                    px: 2,
                                    borderRadius: 2,
                                    height: 40,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            <Card>
                <Box sx={{ mt: 1 }}>
                    <CropHubTable
                        key={refreshKey} 
                        fieldVisits={fieldVisits}
                        searchQuery={searchValue}
                        loading={loading}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={pageSizeOptions}
                        // ─── CHANGE 4: Pass permissions to table ─────────────────────────────────
                        canView={can('view')}
                        canEdit={can('edit')}
                        canDelete={can('delete')}
                        // ───────────────────────────────────────────────────────────────────────
                    />
                </Box>
            </Card>

            <CropHubAddUpdate  
                open={exportModalOpen}
                onClose={handleExportModalClose}
                onRefresh={handleRefresh} 
            />
        </Box>
    );
};

export default CropHub;